import type { MessageKey } from "@wato/i18n";
import {
  ALWAYS_VISIBLE,
  labelsFor,
  visibilityFor,
  navKeyAllowed,
  type ModuleId,
  type LabelTerm,
  type Visibility,
} from "@wato/business-types";

/** FB2-05: a gated item stays visible but locked; `to` is the page that explains how to unlock it. */
export interface NavLock {
  reasonKey: MessageKey;
  /** Relative to `/w/:workspaceId`, like `NavItem.to`. */
  to: string;
}

/** UX-02: sidebar information architecture as data. `to` is relative to `/w/:workspaceId`. */
export interface NavItem {
  to: string;
  label: MessageKey;
  end?: boolean;
  /** Shown when the member holds one of these permissions (UI hint; API enforces). */
  anyOf?: string[];
  /** WP-321: locked (still visible) unless the workspace has a catalog-capable channel. */
  needsCatalog?: boolean;
  /** WP-316: locked (still visible) unless the plan entitles this feature (server-resolved). */
  needsFeature?: string;
  /** UXR-11: rarely needed page; stays reachable, but sits in the collapsed "Advanced" part of its section. */
  advanced?: boolean;
  /** UX audit #3: one of the few tabs shown first in a grouped section; the rest sit under "More". */
  essential?: boolean;
  /** OWN-174: heading block this tab sits under in a grouped section tab bar (no group = flat tabs / lead tab). */
  group?: "ai" | "replies" | "messages";
  /** OWN-178: a page that shares another page's tab (the host's tab stays highlighted here and the page has no tab of its own while the host tab is shown). */
  tabHost?: string;
  /** OWN-283: live count shown next to the tab label. */
  badge?: "flowErrors";
  /** UXR-11: also listed in these other groups' sections (the page keeps one home for highlighting). */
  alsoIn?: readonly string[];
  /** FB3-22: on a top item, the NAV_GROUPS ids whose pages are its tabs (settings: its side list). */
  groupIds?: readonly string[];
  /** Computed by `filterNav` for gated items; never set in the config below. */
  locked?: NavLock;
}
/** UXR-11: Home, the follow-up dashboard, Inbox, Channels, deals, calendar, the six tasks, «جيب عملاء» and Settings; every other page lives in a section's tabs. */
// OWN-368: +1 for the «جيب عملاء» section.
export const NAV_VISIBLE_LIMIT = 14;
/** FB2-50: the simplified sidebar of an inbox/contacts-only member. */
const INBOX_ONLY = new Set(["conversations", "groups", "contacts", "help-center"]);
/** OWN-175: conversations-only staff see the inbox (and help) and nothing else. */
const STAFF_ONLY_NAV = new Set(["conversations", "help-center"]);
/** Permissions that mean "more than inbox + contacts"; holding none of them = simplified nav. */
const BEYOND_INBOX = [
  "flows.view",
  "sequences.view",
  "broadcasts.view",
  "analytics.view",
  "ai_agents.view",
  "integrations.view",
  "roles.view",
  "audit.view",
];

/** A member who can only work the inbox/contacts (e.g. the agent role). UX only: routes stay guarded. */
export function isInboxOnlyRole(granted: readonly string[] | undefined): boolean {
  if (!granted) return false;
  return (
    (granted.includes("inbox.view") || granted.includes("contacts.view")) &&
    !BEYOND_INBOX.some((p) => granted.includes(p))
  );
}

/** OWN-175: the permissions of the "conversations-only staff" presets (mirrors STAFF_PERMISSIONS). */
const STAFF_ONLY_PERMISSIONS: ReadonlySet<string> = new Set([
  "workspace.view",
  "channels.view",
  "inbox.view",
  "inbox.create",
  "inbox.edit",
  "inbox.assign",
]);

/** Pages (relative to `/w/:id`) a conversations-only staff member may open besides the inbox. */
const STAFF_ONLY_PAGES = ["conversations", "help-center", "notifications", "settings/account"];
/** Account settings and notification preferences stay reachable for everyone. */
const STAFF_ONLY_EXACT = ["settings/notifications"];

/** OWN-175: holds the inbox and nothing beyond it. UX only: the API enforces the same set. */
export function isConversationsOnlyStaff(granted: readonly string[] | undefined): boolean {
  if (!granted || !granted.includes("inbox.view")) return false;
  return granted.every((p) => STAFF_ONLY_PERMISSIONS.has(p));
}

/** OWN-175: whether a staff-only member may open `rel` (path below `/w/:id`). */
export function staffMayOpen(rel: string): boolean {
  return (
    STAFF_ONLY_EXACT.includes(rel) ||
    STAFF_ONLY_PAGES.some((page) => rel === page || rel.startsWith(`${page}/`))
  );
}

export interface NavGroup {
  id: string;
  label: MessageKey;
  items: readonly NavItem[];
}

/**
 * UXR-11: the sidebar items: Home, the Inbox (always one click), the six task groups of
 * docs/ux-redesign-sitemap.md §3, and Settings. A top item opens its landing page; the other pages of
 * its section (`groupIds` into NAV_GROUPS) show as tabs above the content, so every route stays reachable.
 */
export const NAV_TOP: readonly NavItem[] = [
  { to: "", label: "dashboard.nav.home", end: true },
  { to: "conversations", label: "dashboard.nav.conversations" },
  // Spec §7.4: channels is never hidden, so it is its own sidebar item, not only a Reply tab.
  { to: "channels", label: "channels.nav", anyOf: ["channels.view"] },
  { to: "contacts", label: "dashboard.nav.taskReply", groupIds: ["reply"] },
  // B29-05: deals and calendar are standalone pages, no longer tabs under customers.
  { to: "pipeline", label: "dashboard.nav.deals", anyOf: ["contacts.view"] },
  { to: "calendar", label: "own101.calendarNav", anyOf: ["contacts.view"] },
  // OWN-46: the AI hub is the landing page of this section, so it is visible in the sidebar.
  { to: "ai", label: "dashboard.nav.taskAutomate", groupIds: ["automate"] },
  { to: "broadcasts", label: "dashboard.nav.b29b.campaigns", groupIds: ["offer"] },
  // OWN-368: the growth tools are their own section; its landing page is the first tab.
  { to: "growth/widget", label: "dashboard.nav.b29b.growth", groupIds: ["growth"] },
  { to: "store", label: "dashboard.nav.taskSell", groupIds: ["sell"] },
  { to: "bookings", label: "dashboard.nav.taskBookings", groupIds: ["bookings"] },
  { to: "analytics", label: "dashboard.nav.taskNumbers", groupIds: ["numbers"] },
  {
    to: "settings",
    label: "dashboard.nav.groupSettings",
    groupIds: [
      "account",
      "team",
      "channels",
      "aiProviders",
      "billing",
      "integrations",
      "advanced",
      "help",
    ],
  },
];

/** The top item whose section owns `relPath` (Home for the empty path). */
export function topItemForPath(relPath: string): NavItem | undefined {
  const own = NAV_TOP.find(
    (t) => !t.groupIds && t.to !== "" && (relPath === t.to || relPath.startsWith(`${t.to}/`)),
  );
  if (own) return own;
  const group = groupForPath(relPath);
  if (group) return NAV_TOP.find((t) => t.groupIds?.includes(group));
  return NAV_TOP.find((t) =>
    t.to === "" ? relPath === "" : relPath === t.to || relPath.startsWith(`${t.to}/`),
  );
}

/**
 * UXR-11: the single source for the sidebar, the section tabs and the command palette. Every label
 * (group headings included) is unique in both locales; the `nav-config` test enforces it. Group ids
 * are stable. Items flagged `advanced` stay reachable but sit in a collapsed part of their section.
 */
export const NAV_GROUPS: readonly NavGroup[] = [
  {
    id: "reply",
    label: "dashboard.nav.taskReply",
    items: [{ to: "contacts", label: "dashboard.nav.contacts", end: true }],
  },
  {
    id: "automate",
    label: "dashboard.nav.taskAutomate",
    items: [
      {
        to: "system-replies",
        group: "replies",
        label: "dashboard.nav.autoReplies",
        anyOf: ["flows.view"],
        needsFeature: "flows.builder",
        essential: true,
      },
      { to: "ai", group: "ai", label: "aiHub.nav", anyOf: ["ai_agents.view"], essential: true },
      { to: "gallery", group: "replies", label: "gallery.nav" },
      { to: "media", group: "messages", label: "media.nav", anyOf: ["flows.view"] },
      {
        to: "flows",
        group: "replies",
        label: "flows.nav",
        anyOf: ["flows.view"],
        needsFeature: "flows.builder",
        essential: true,
      },
      {
        to: "forms",
        group: "messages",
        label: "dashboard.nav.forms",
        anyOf: ["flows.view"],
        essential: true,
      },
      {
        to: "automations/order-lifecycle",
        group: "messages",
        label: "dashboard.nav.orderNotifications",
        anyOf: ["flows.view"],
      },
      {
        to: "ai-agents/test-console",
        group: "ai",
        label: "dashboard.nav.testConsole",
        anyOf: ["ai_agents.view"],
      },
      {
        to: "automations/errors",
        group: "replies",
        label: "flows.b29errors.nav",
        anyOf: ["flows.view"],
        needsFeature: "flows.builder",
        badge: "flowErrors",
      },
      {
        to: "automations/control",
        group: "replies",
        label: "flowControl.nav",
        anyOf: ["flows.view"],
        needsFeature: "flows.builder",
        advanced: true,
      },
      {
        to: "automations/analytics",
        label: "automationAdmin.analytics.title",
        anyOf: ["flows.view"],
        advanced: true,
      },
      {
        to: "automations/settings",
        label: "automationAdmin.settings.title",
        anyOf: ["flows.view"],
        advanced: true,
      },
      { to: "ai/kb-fix-queue", label: "kbFix.nav", anyOf: ["ai_agents.view"], advanced: true },
      { to: "ai/faq-mining", label: "faqMining.nav", anyOf: ["ai_agents.view"], advanced: true },
    ],
  },
  {
    id: "offer",
    label: "dashboard.nav.b29b.campaigns",
    items: [
      { to: "broadcasts", label: "broadcasts.nav", anyOf: ["broadcasts.view"] },
      // OWN-368: sequences moved here from «automate»; the page itself is unchanged.
      { to: "sequences", label: "sequences.nav", anyOf: ["sequences.view"] },
      {
        to: "vouchers",
        label: "dashboard.nav.vouchers",
        anyOf: ["flows.view"],
        needsFeature: "flows.builder",
      },
      { to: "loyalty", label: "dashboard.loyalty.nav", anyOf: ["workspace.view"] },
      { to: "prospector", label: "prospector.nav", anyOf: ["prospector.view"] },
      {
        to: "sell/winback",
        label: "dashboard.nav.winback",
        anyOf: ["commerce.view"],
        needsFeature: "ai.agent",
      },
      {
        to: "marketplace",
        label: "dashboard.nav.marketplace",
        anyOf: ["commerce.view"],
        advanced: true,
      },
      {
        to: "marketplace/submit",
        label: "dashboard.nav.marketplaceSubmit",
        anyOf: ["commerce.create"],
      },
    ],
  },
  {
    id: "growth",
    label: "dashboard.nav.b29b.growth",
    items: [
      { to: "growth/widget", label: "widget.nav", anyOf: ["workspace.view"] },
      { to: "growth/site-forms", label: "siteForms.nav", anyOf: ["workspace.view"] },
      { to: "growth/short-links", label: "shortLinks.nav", anyOf: ["workspace.view"] },
      { to: "growth/social-posts", label: "socialPosts.nav", anyOf: ["channels.view"] },
    ],
  },
  {
    id: "sell",
    label: "dashboard.nav.taskSell",
    // OWN-346: one entry; the store page carries its own six tabs.
    items: [{ to: "store", label: "dashboard.nav.productsCatalog", anyOf: ["commerce.view"] }],
  },
  {
    id: "bookings",
    label: "dashboard.nav.taskBookings",
    items: [
      { to: "bookings", label: "dashboard.nav.bookingsList", anyOf: ["commerce.view"], end: true },
      { to: "bookings/services", label: "bookings.nav.services", anyOf: ["commerce.view"] },
      { to: "bookings/staff", label: "bookings.nav.staff", anyOf: ["commerce.view"] },
      { to: "bookings/waitlist", label: "bookings.nav.waitlist", anyOf: ["commerce.view"] },
      { to: "bookings/settings", label: "bookings.nav.settings", anyOf: ["commerce.view"] },
      // OWN-368: trips moved here from the campaigns section. OWN-418: departures live in Packages.
      { to: "trips/packages", label: "dashboard.packages.nav", anyOf: ["trips.view"] },
      { to: "trips/bookings", label: "dashboard.tripBookings.nav", anyOf: ["trips.view"] },
    ],
  },
  {
    id: "numbers",
    label: "dashboard.nav.taskNumbers",
    items: [
      {
        to: "analytics",
        label: "dashboard.nav.analyticsOverview",
        anyOf: ["analytics.view"],
        end: true,
      },
      { to: "analytics/sales", label: "dashboard.nav.sales", anyOf: ["analytics.view"] },
      { to: "analytics/payments", label: "analytics.payments.title", anyOf: ["analytics.view"] },
      {
        to: "analytics/delivery",
        label: "analytics.delivery.title",
        anyOf: ["analytics.view"],
      },
      {
        to: "analytics/entry-points",
        label: "dashboard.nav.entryPoints",
        anyOf: ["analytics.view"],
      },
    ],
  },
  {
    id: "account",
    label: "dashboard.nav.setAccount",
    items: [
      { to: "settings/workspace", label: "dashboard.nav.workspaceSettings" },
      // B29-05: message templates live with the conversation settings, not under offers.
      { to: "templates", label: "dashboard.nav.messageTemplates", anyOf: ["templates.view"] },
      {
        to: "settings/notifications",
        label: "notifications.prefs.nav",
        anyOf: ["workspace.view"],
      },
      {
        to: "settings/menu",
        label: "menuSettings.nav",
        anyOf: ["workspace.view"],
      },
      {
        to: "settings/order-status-messages",
        label: "restaurantStatus.nav",
        anyOf: ["workspace.view"],
      },
      {
        to: "settings/features",
        label: "featureSwitches.nav",
        anyOf: ["workspace.edit"],
      },
      {
        to: "settings/balance-followups",
        label: "balanceFollowUps.nav",
        anyOf: ["accounts.view"],
      },
      {
        to: "settings/branches",
        label: "branches.nav",
        anyOf: ["workspace.view"],
        needsFeature: "multi_branch.enabled",
      },
      { to: "migration", label: "dashboard.nav.migration", anyOf: ["workspace.view"] },
    ],
  },
  {
    id: "team",
    label: "dashboard.nav.setTeam",
    items: [
      { to: "settings/team", label: "team.nav", anyOf: ["team.view"] },
      { to: "settings/teams", label: "routing.nav.teams", anyOf: ["team.view"] },
      { to: "settings/roles", label: "roles.nav", anyOf: ["roles.view"] },
      { to: "settings/approvals", label: "team.approvals.nav", anyOf: ["team.view"] },
    ],
  },
  {
    id: "channels",
    label: "own111.groups.channels",
    items: [
      {
        to: "settings/channels/sms-fallback",
        label: "smsFallback.title",
        anyOf: ["integrations.view"],
        advanced: true,
      },
    ],
  },
  {
    id: "aiProviders",
    label: "own111.groups.ai",
    items: [
      {
        to: "settings/ai/byok",
        label: "dashboard.nav.byok",
        anyOf: ["ai_agents.view"],
        advanced: true,
      },
    ],
  },
  {
    id: "billing",
    label: "own111.groups.billing",
    items: [
      { to: "settings/billing", label: "billing.nav" },
      { to: "guarantee", label: "dashboard.guarantee.nav", anyOf: ["billing.view"] },
    ],
  },
  {
    id: "integrations",
    label: "own111.groups.integrations",
    items: [
      {
        to: "settings/integrations/hub",
        label: "integrationsHub.nav",
        anyOf: ["integrations.view"],
      },
      {
        to: "settings/api-keys",
        label: "apiKeys.nav",
        anyOf: ["integrations.view"],
        advanced: true,
      },
      {
        to: "settings/integrations/webhooks",
        label: "webhooks.title",
        anyOf: ["integrations.view"],
        advanced: true,
      },
      {
        to: "settings/integrations/google-sheets",
        label: "googleSheets.nav",
        anyOf: ["integrations.view"],
        advanced: true,
      },
      {
        to: "settings/integrations/doc-templates",
        label: "docTemplates.nav",
        anyOf: ["integrations.view"],
        advanced: true,
      },
      {
        // The n8n / Zapier gallery lives under Settings -> Integrations, not in Automation.
        to: "settings/integrations/automations",
        label: "dashboard.nav.n8nTemplates",
        anyOf: ["integrations.view"],
        advanced: true,
      },
      {
        to: "settings/integrations/credentials",
        label: "credentialVault.nav",
        anyOf: ["integrations.view"],
        advanced: true,
      },
    ],
  },
  {
    id: "advanced",
    label: "dashboard.nav.advanced",
    items: [
      { to: "settings/otp", label: "otp.nav", anyOf: ["integrations.view"], advanced: true },
      { to: "settings/audit-log", label: "auditLog.nav", anyOf: ["audit.view"], advanced: true },
    ],
  },
  {
    id: "help",
    label: "dashboard.nav.setHelp",
    items: [
      { to: "getting-started", label: "setup.checklist.link" },
      { to: "help-center", label: "helpCenter.nav" },
      { to: "help-center/videos", label: "helpCenter.videos.title" },
      { to: "support", label: "dashboard.support.title" },
    ],
  },
];

export interface NavFilter {
  catalogAvailable: boolean;
  gatedFeatures: Record<string, boolean | undefined>;
  granted: readonly string[] | undefined;
  /** ACT-04: the workspace's account type (`null`/absent = general, everything shows). */
  businessType?: string | null | undefined;
  /** ACT-04: feature keys that already hold data; never hidden by type (see `useFeaturesWithData`). */
  hasData?: ReadonlySet<string> | undefined;
  /** b80: owner module flags; a disabled module hides its nav items (links and data keep working). */
  modules?: Readonly<Record<ModuleId, boolean>> | undefined;
  /** Prospector is server flag-gated: its item shows only when the probe said `on` (see `useProspectorEnabled`). */
  prospectorEnabled?: boolean | undefined;
}

/** The `@wato/business-types` feature key of a nav item (`""` = home; a section top = its task card). */
export function navFeatureKey(item: NavItem): string {
  if (item.to === "") return "home";
  if (item.groupIds && !ALWAYS_VISIBLE.has(item.to) && item.to !== "settings") {
    return `task:${item.groupIds[0]}`;
  }
  return item.to;
}

/** How the workspace's account type treats a nav item; always-visible pages are never touched. */
export function navTypeVisibility(item: NavItem, f: NavFilter): Visibility {
  if (!navKeyAllowed(f.modules, navFeatureKey(item))) return "hide";
  return visibilityFor(
    f.businessType,
    navFeatureKey(item),
    f.hasData ? { hasData: f.hasData } : {},
  );
}

const MARKETPLACE_SUBMIT_ROUTE = "marketplace/submit";
/** OWN-373: platform entitlement, default off; Super Admin turns it on per workspace. */
export const MARKETPLACE_SUBMIT_FEATURE = "marketplace.submit";

/** Permission gate only: plan/availability gates lock an item (see `navItemLock`), never hide it. */
export function isNavItemVisible(item: NavItem, f: NavFilter): boolean {
  if (item.to === "prospector" && f.prospectorEnabled !== true) return false;
  // OWN-373: «أضف حلّك» is hidden from normal customers; admin enables it per workspace.
  if (item.to === MARKETPLACE_SUBMIT_ROUTE && f.gatedFeatures[MARKETPLACE_SUBMIT_FEATURE] !== true)
    return false;
  return !item.anyOf || item.anyOf.some((p) => f.granted?.includes(p));
}

/**
 * Why a visible item is locked (plan first, then Meta catalog availability) and where the member
 * goes to unlock it; `undefined` when it is open. An unknown plan feature counts as entitled.
 */
export function navItemLock(item: NavItem, f: NavFilter): NavLock | undefined {
  if (item.needsFeature && f.gatedFeatures[item.needsFeature] === false) {
    return { reasonKey: "dashboard.nav.locked.plan", to: "settings/billing" };
  }
  if (item.needsCatalog && !f.catalogAvailable) {
    return { reasonKey: "dashboard.nav.locked.metaCatalog", to: "channels" };
  }
  return undefined;
}

/** ACT-05: the term an item carries, for per-type label overrides (section tops are never renamed). */
const TERM_BY_ROUTE: Record<string, LabelTerm> = { contacts: "contacts", pipeline: "pipeline" };

/** ACT-05: the item with the account type's label override, or unchanged when it has none. */
export function withTypeLabel(item: NavItem, type: string | null | undefined): NavItem {
  if (item.groupIds) return item;
  const term = TERM_BY_ROUTE[item.to];
  const key = term ? labelsFor(type)[term] : undefined;
  return key ? { ...item, label: key as MessageKey } : item;
}

function withLock(item: NavItem, f: NavFilter): NavItem {
  const locked = navItemLock(item, f);
  const labelled = withTypeLabel(item, f.businessType);
  return locked ? { ...labelled, locked } : labelled;
}

export interface FilteredNav {
  top: NavItem[];
  /** FB3-22: visible pages per top item (keyed by `NavItem.to`), shown as tabs. */
  sections: Record<string, NavItem[]>;
  /** FB2-50: true when only inbox/contacts/help are shown. */
  simplified: boolean;
  /** ACT-04: pages the member may open that the account type hides; only the search lists them. */
  notEnabled: NavItem[];
}

/** A top item shows when the member can open any page of its section. */
export function filterNav(f: NavFilter): FilteredNav {
  const all = NAV_GROUPS.flatMap((g) =>
    g.items.flatMap((i) => [g.id, ...(i.alsoIn ?? [])].map((id) => ({ g: id, i }))),
  );
  if (isInboxOnlyRole(f.granted)) {
    return {
      top: [...NAV_TOP.filter((i) => !i.groupIds), ...NAV_GROUPS.flatMap((g) => g.items)]
        .filter(
          (i) =>
            (isConversationsOnlyStaff(f.granted) ? STAFF_ONLY_NAV : INBOX_ONLY).has(i.to) &&
            isNavItemVisible(i, f),
        )
        .map((i) => withLock(i, f)),
      sections: {},
      simplified: true,
      notEnabled: [],
    };
  }
  const sections: Record<string, NavItem[]> = {};
  const top: NavItem[] = [];
  const notEnabled: NavItem[] = [];
  for (const item of NAV_TOP) {
    if (item.groupIds) {
      const visible = all
        .filter((x) => item.groupIds?.includes(x.g) && isNavItemVisible(x.i, f))
        .map((x) => ({ ...x, v: navTypeVisibility(x.i, f) }));
      const pages = visible
        .filter((x) => x.v !== "hide")
        .map((x) => withLock(x.v === "more" ? { ...x.i, advanced: true } : x.i, f));
      notEnabled.push(
        ...visible.filter((x) => x.v === "hide").map((x) => withTypeLabel(x.i, f.businessType)),
      );
      if (pages.length === 0) continue;
      if (navTypeVisibility(item, f) === "hide") {
        notEnabled.push(item, ...pages);
        continue;
      }
      sections[item.to] = pages;
      top.push(item);
      continue;
    }
    // B29-05: standalone items (deals, calendar) keep their per-business-type label and visibility.
    if (!isNavItemVisible(item, f)) continue;
    if (navTypeVisibility(item, f) === "hide") {
      notEnabled.push(withTypeLabel(item, f.businessType));
      continue;
    }
    top.push(withTypeLabel(item, f.businessType));
  }
  return { top, sections, simplified: false, notEnabled };
}

/** The group owning the longest-matching route for `relPath` (relative to the workspace). */
export function groupForPath(relPath: string): string | null {
  let best: { id: string; len: number } | null = null;
  for (const g of NAV_GROUPS) {
    for (const i of g.items) {
      const hit = relPath === i.to || relPath.startsWith(`${i.to}/`);
      if (hit && (!best || i.to.length > best.len)) best = { id: g.id, len: i.to.length };
    }
  }
  return best?.id ?? null;
}

/**
 * feat/ui-simplify: the sidebar's top items in a few plain-language sections. "Every day" is what
 * a merchant opens daily; "Grow sales" holds the selling tools; "More" (collapsed until opened,
 * or while one of its pages is open) holds reports and settings. Unknown top items land in
 * "Grow sales" so a new item is never lost.
 */
interface NavSection {
  readonly id: "daily" | "grow" | "more";
  readonly label: MessageKey;
  readonly collapsible: boolean;
  readonly items: NavItem[];
}

const SECTION_OF: Readonly<Record<string, NavSection["id"]>> = {
  "": "daily",
  conversations: "daily",
  channels: "daily",
  contacts: "daily",
  pipeline: "daily",
  calendar: "daily",
  ai: "grow",
  broadcasts: "grow",
  "growth/widget": "grow",
  store: "grow",
  bookings: "grow",
  analytics: "more",
  settings: "more",
};

const SECTION_META: readonly Omit<NavSection, "items">[] = [
  { id: "daily", label: "dashboard.nav.section.daily", collapsible: false },
  { id: "grow", label: "dashboard.nav.section.grow", collapsible: false },
  { id: "more", label: "dashboard.nav.section.more", collapsible: true },
];

/** Groups visible top items into sections (order kept, empty sections dropped). */
export function sidebarSections(top: readonly NavItem[]): NavSection[] {
  return SECTION_META.map((meta) => ({
    ...meta,
    items: top.filter((i) => (SECTION_OF[i.to] ?? "grow") === meta.id),
  })).filter((s) => s.items.length > 0);
}
